import { getRandomElementFromArray } from './common'
import { retryPromise } from './promise'

/**
 * Get all font variants that fit between the min and max weight
 * @param {Array} variant_map array of font variants to filter
 * @param {string} style either italic or normal
 * @param {number} min_weight minimum font weight to use
 * @param {number} max_weight maximum font weight to use
 * @returns
 */
export const getValidFontVariants = (
  variant_map,
  style,
  min_weight,
  max_weight
) =>
  variant_map.filter(
    font =>
      font.numeric >= min_weight &&
      font.numeric <= max_weight &&
      font.style === style
  )

export const selectRandomFontVariant = (
  family,
  style,
  min_weight,
  max_weight
) => {
  const { variant_map } = family
  const valid_variants = getValidFontVariants(
    variant_map,
    style,
    min_weight,
    max_weight
  )
  return getRandomElementFromArray(valid_variants)
}

export const selectFontVariantsForPreloading = (font_families, filters) => {
  const { max_font_weight, min_font_weight, style } = filters
  return font_families.map(family => {
    const selected_variant = selectRandomFontVariant(
      family,
      style,
      min_font_weight,
      max_font_weight
    )
    return {
      family,
      selected_variant,
    }
  })
}

export const createFontFace = async (name, url, opts) =>
  new Promise(async (resolve, reject) => {
    try {
      const fontName = name.replace(/[0-9]/g, '')
      const font = new FontFace(fontName, `url(${url})`, opts)

      await font.load()
      document.fonts.add(font)
      resolve()
    } catch (e) {
      reject(e)
    }
  })

/**
 * uses FontFace web api to add a remote font to document
 * @param {string} name name of fonts which will be used in css
 * @param {string}
 */
const addFontToDocument = async (name, url, opts) =>
  new Promise(async (resolve, reject) => {
    if (!name || !url)
      throw new Error(`Cannot load font with name ${name} and url ${url}`)
    try {
      await retryPromise(createFontFace, [name, url, opts])
      resolve()
    } catch (error) {
      reject(error)
    }
  })

/**
 * Grab the default font from a family. If no "normal" variant is found, just return the first one
 *
 * @param {object} family A font family containing variants
 */
const getDefaultFontFromFamily = ({ variant_map }) => {
  let regular = variant_map.find(({ variant }) => variant === 'regular')
  if (!regular) {
    regular = variant_map.find(font => font.style === 'normal')
  }
  if (!regular) {
    regular = variant_map[0]
  }
  const fontName = regular.name

  if (!regular) {
    throw new Error(`No regular weight found for ${fontName}`)
  }

  return {
    name: fontName,
    url: regular.cdn_url,
  }
}

/**
 * Map detailed font weights to light, regular, and bold.
 * ⚠️ These are specifically ordered by preference.
 */
const weightMap = {
  light: ['300', 'light', '200', 'extralight', '100', 'thin'],
  regular: ['400', 'regular', '500', 'medium'],
  bold: [
    '700',
    'bold',
    '800',
    'extrabold',
    '600',
    'semibold',
    '900',
    'black',
    '950',
    'extrablack',
  ],
}

/**
 * Map weight values to names
 */
const weightNames = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
  950: 'Extra Black',
}

/**
 * Map weight names to values
 */
const weightValues = {
  thin: 100,
  extralight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
  extrablack: 950,
}

/**
 * Get a Fonts object from a Logo
 * @param {object} logo A logo
 * @param {'wordmark' | 'slogan'} scope Whether we're looking for wordmark or slogan
 * @param {object} $api The API instance
 */
const getFontFromLogo = async (logo = {}, scope, $api) => {
  const key = `${scope}_fonts`

  // Try and set the font using logo_preset style
  let id = logo[key] || null

  // If we don't have an ID, it might be a post-purchase logo
  // We need to double check typeof as some logos have a logo.wordmark_fonts (Array) key that we don't want
  if (!id || typeof id !== 'string') {
    const { assets } = logo
    id = assets && assets[key] && assets[key][0] ? assets[key][0].id : null
  }

  let font = null

  if (id) {
    try {
      const { data } = await $api.get(`/api/v3/assets/fonts/${id}?url=true`)

      font = data
    } catch (error) {
      console.error(error)
    }
  }

  return font || {}
}

/**
 * Get all Fonts from a Logo
 * @param {object} logo A logo
 * @param {object} $api The API instance
 */
const getAllFontsFromLogo = async (logo, $api) => ({
  wordmark: await getFontFromLogo(logo, 'wordmark', $api),
  slogan: await getFontFromLogo(logo, 'slogan', $api),
})

export {
  getDefaultFontFromFamily,
  getAllFontsFromLogo,
  addFontToDocument,
  getFontFromLogo,
  weightValues,
  weightNames,
  weightMap,
}
